import { supabase } from "@/integrations/supabase/client";
import { PaginatedResponse, PaginationParams } from "@/types";
import { SupabaseClient } from "@supabase/supabase-js";

export interface UserBookFeedback {
  id: number;
  user_id: number;
  book_id: number;
  content_rating: number | null;
  audio_rating: number | null;
  overall_rating: number | null;
  optional_review: string | null;
  would_recommend: boolean | null;
  completion_percentage: number | null;
  created_at: string;
  updated_at: string;
  is_hidden: boolean;
  hidden_reason: string | null;
  hidden_at: string | null;
  hidden_by: number | null;
  profiles: {
    id: number;
    uid: string;
    email: string;
    name: string | null;
  };
  books: {
    id: number;
    title: string;
    authors: Array<{
      id: number;
      name: string;
      email: string | null;
    }> | null;
  };
  hidden_by_profile?: {
    id: number;
    name: string | null;
    email: string;
  } | null;
}

export interface FeedbackStats {
  totalFeedback: number;
  monthlyFeedback: number;
  averageRating: number;
  recommendationRate: number;
}

export const fetchUserBookFeedback = async (
  pagination: PaginationParams = { page: 1, pageSize: 10 },
  filters: { rating?: string; recommendation?: string } = {}
): Promise<PaginatedResponse<UserBookFeedback>> => {
  const client = supabase as SupabaseClient;
  const { page = 1, pageSize = 10 } = pagination;

  // Specify the foreign key relationship explicitly
  const baseSelect = `*, 
       profiles!user_book_feedback_user_id_fkey(id, uid, email, name), 
       books(
         id, 
         title,
         books_authors(
           people(id, name, email)
         )
       )`;

  let query = client
    .from("user_book_feedback")
    .select(baseSelect, { count: "exact" })
    .order("created_at", { ascending: false });

  if (filters.rating && filters.rating !== "all") {
    const rating = parseInt(filters.rating, 10);
    query = query.eq("overall_rating", rating);
  }
  if (filters.recommendation && filters.recommendation !== "all") {
    query = query.eq(
      "would_recommend",
      filters.recommendation === "yes"
    );
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const { data, error, count } = await query.range(from, to);

  if (error) {
    console.error("Error fetching user book feedback:", error);
    throw error;
  }

  // Transform the data to flatten the authors array
  const transformedData = (data as Array<UserBookFeedback & {
    books: {
      books_authors: Array<{
        people: {
          id: number;
          name: string;
          email: string | null;
        } | null;
      }> | null;
    } | null;
  }>)?.map((item) => ({
    ...item,
    books: {
      ...item.books,
      authors: item.books?.books_authors?.map((ba) => ba.people).filter(Boolean) || null
    }
  })) || [];

  return {
    data: transformedData as UserBookFeedback[],
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.ceil((count || 0) / pageSize),
  };
};

export const fetchBookFeedback = async (
  bookId: number,
  pagination: PaginationParams = { page: 1, pageSize: 10 },
  filters: { rating?: string; recommendation?: string; sortBy?: string; showHidden?: boolean } = {}
): Promise<PaginatedResponse<UserBookFeedback>> => {
  const client = supabase as SupabaseClient;
  const { page = 1, pageSize = 10 } = pagination;

  // Specify the foreign key relationships explicitly since there are multiple FKs to profiles
  const baseSelect = `*, 
       profiles!user_book_feedback_user_id_fkey(id, uid, email, name),
       hidden_by_profile:profiles!user_book_feedback_hidden_by_fkey(id, name, email)`;

  let query = client
    .from("user_book_feedback")
    .select(baseSelect, { count: "exact" })
    .eq("book_id", bookId);

  // By default, only show non-hidden feedback (for user frontend)
  // Admin can explicitly request to see hidden feedback
  if (!filters.showHidden) {
    query = query.eq("is_hidden", false);
  }

  // Apply filters
  if (filters.rating && filters.rating !== "all") {
    const rating = parseInt(filters.rating, 10);
    query = query.eq("overall_rating", rating);
  }
  if (filters.recommendation && filters.recommendation !== "all") {
    query = query.eq(
      "would_recommend",
      filters.recommendation === "yes"
    );
  }

  // Apply sorting
  const sortBy = filters.sortBy || "newest";
  switch (sortBy) {
    case "newest":
      query = query.order("created_at", { ascending: false });
      break;
    case "oldest":
      query = query.order("created_at", { ascending: true });
      break;
    case "highest":
      query = query.order("overall_rating", { ascending: false, nullsFirst: false });
      break;
    case "lowest":
      query = query.order("overall_rating", { ascending: true, nullsFirst: false });
      break;
    default:
      query = query.order("created_at", { ascending: false });
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const { data, error, count } = await query.range(from, to);

  if (error) {
    console.error("Error fetching book feedback:", error);
    throw error;
  }

  return {
    data: (data as UserBookFeedback[]) || [],
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.ceil((count || 0) / pageSize),
  };
};

export const toggleFeedbackVisibility = async (
  feedbackId: number,
  isHidden: boolean,
  hiddenReason?: string,
  hiddenBy?: number
): Promise<void> => {
  const client = supabase as SupabaseClient;

  const updateData: {
    is_hidden: boolean;
    hidden_reason?: string | null;
    hidden_at?: string | null;
    hidden_by?: number | null;
  } = {
    is_hidden: isHidden,
  };

  if (isHidden) {
    // If hiding, set the reason, timestamp, and admin
    updateData.hidden_reason = hiddenReason || null;
    updateData.hidden_at = new Date().toISOString();
    updateData.hidden_by = hiddenBy || null;
  } else {
    // If unhiding, clear the moderation fields
    updateData.hidden_reason = null;
    updateData.hidden_at = null;
    updateData.hidden_by = null;
  }

  const { error } = await client
    .from("user_book_feedback")
    .update(updateData)
    .eq("id", feedbackId);

  if (error) {
    console.error("Error toggling feedback visibility:", error);
    throw error;
  }
};

export const fetchUserBookFeedbackStats = async (): Promise<FeedbackStats> => {
  const client = supabase as SupabaseClient;

  const { count: totalCount, error: totalError } = await client
    .from("user_book_feedback")
    .select("id", { count: "exact", head: true });
  if (totalError) throw totalError;

  const currentMonth = new Date();
  currentMonth.setDate(1);
  currentMonth.setHours(0, 0, 0, 0);
  const { count: monthlyCount, error: monthlyError } = await client
    .from("user_book_feedback")
    .select("id", { count: "exact", head: true })
    .gte("created_at", currentMonth.toISOString());
  if (monthlyError) throw monthlyError;

  const { data: ratingsData, error: ratingsError } = await client
    .from("user_book_feedback")
    .select("overall_rating")
    .not("overall_rating", "is", null);
  if (ratingsError) throw ratingsError;

  const { data: recommendationsData, error: recommendationsError } = await client
    .from("user_book_feedback")
    .select("would_recommend")
    .not("would_recommend", "is", null);
  if (recommendationsError) throw recommendationsError;

  const averageRating =
    ratingsData && ratingsData.length > 0
      ?
        ratingsData.reduce((sum, item) => sum + (item.overall_rating || 0), 0) /
        ratingsData.length
      : 0;
  const recommendationRate =
    recommendationsData && recommendationsData.length > 0
      ?
        (recommendationsData.filter((i) => i.would_recommend).length /
          recommendationsData.length) * 100
      : 0;

  return {
    totalFeedback: totalCount || 0,
    monthlyFeedback: monthlyCount || 0,
    averageRating: Math.round(averageRating * 10) / 10,
    recommendationRate: Math.round(recommendationRate),
  };
};
